import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Clock, Check, MessageCircle } from 'lucide-react';
import { ACADEMY_COURSE_MEDIA, WHATSAPP_URL } from './data';
import { useLanguage } from './i18n/LanguageContext';
export function Academy() {
  const {
    t
  } = useLanguage();
  const coursePlans = t.academy.courses.map((course, index) => ({
    ...course,
    ...ACADEMY_COURSE_MEDIA[index]
  }));
  const whatsappHref = `${WHATSAPP_URL}?text=${encodeURIComponent(t.academy.whatsappMessage)}`;
  return <section id="academia" className="max-w-2xl mx-auto px-6 pt-16 scroll-mt-24" data-id="element-17" data-mp="y14sq7lvoa6p">
      <motion.div initial={{
      opacity: 0,
      y: 24
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.7
    }} className="relative bg-dark text-cream p-9 sm:p-11 overflow-hidden" data-id="element-18" data-mp="1c04i5hh7st3x">
        <span aria-hidden className="pointer-events-none absolute -top-8 left-5 font-serif text-[160px] leading-none text-gold/[0.08] select-none" data-id="element-19" data-mp="2ig6odpffltr6">
          &ldquo;
        </span>

        <div className="relative z-10" data-id="element-20" data-mp="3jrqqbh6mayx1">
          <div className="flex items-center gap-3 mb-4" data-id="element-21" data-mp="3jqpf9cplqcfb">
            <GraduationCap className="w-5 h-5 text-gold-light" strokeWidth={1.5} data-id="element-22" data-mp="3oqtvr2qif75j" />
            <p className="font-body font-medium text-[10px] tracking-[5px] uppercase text-gold-light" data-id="element-23" data-mp="3ooqu1lfzvpkz">
              {t.academy.eyebrow}
            </p>
          </div>

          <h3 className="font-serif font-light text-warm-white text-2xl sm:text-[28px] leading-snug mb-4" data-id="element-24" data-mp="2w3h62jp1yj80">
            {t.academy.titleTop}
            <br data-id="element-25" data-mp="30ywntmq6kz9p" />
            {t.academy.titleBottomPlain}
            <em className="italic text-gold-light" data-id="element-26" data-mp="35zkc40n0z208">{t.academy.titleBottomEm}</em>
          </h3>

          <p className="font-body font-light text-cream/75 text-sm leading-[1.85] mb-6" data-id="element-27" data-mp="3aru6ptz34q8u">
            {t.academy.body}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-id="element-28" data-mp="2d9u5ecnq32e5">
            {t.academy.topics.map(topic => <div key={topic} className="flex items-center gap-3 px-4 py-3 bg-gold/[0.12] border border-gold/25" data-id="element-29" data-mp="2i8euph1fs1nt">
                <span className="w-1.5 h-1.5 rounded-full bg-gold-light flex-shrink-0" data-id="element-30" data-mp="25ayzwzo316dw" />
                <span className="font-body text-warm-white text-xs tracking-[0.3px]" data-id="element-31" data-mp="2594jnynra2g5">
                  {topic}
                </span>
              </div>)}
          </div>

          <div className="mt-11" data-id="element-32" data-mp="2f1aah529udtr">
            <div className="flex items-center gap-4 mb-7" data-id="element-33" data-mp="1rb7p17sv4644">
              <span aria-hidden className="h-px w-8 bg-gold/40" data-id="element-34" data-mp="1rbpqqjyancjf" />
              <p className="font-body font-medium text-[10px] tracking-[5px] uppercase text-gold-light" data-id="element-35" data-mp="1mcweeuuqtrku">
                {t.academy.investment}
              </p>
              <span aria-hidden className="h-px flex-1 bg-gold/20" data-id="element-36" data-mp="2170z8kj3zniq" />
            </div>

            <ul className="space-y-3" data-id="element-37" data-mp="1wcdjpb6323ga">
              {coursePlans.map((course, i) => <motion.li key={course.name} initial={{
              opacity: 0,
              y: 14
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-40px'
            }} transition={{
              duration: 0.55,
              delay: 0.08 * i
            }} className={`relative p-6 sm:p-7 border ${course.featured ? 'bg-gold/[0.10] border-gold/40' : 'bg-warm-white/[0.03] border-gold/15'}`} data-id="element-38" data-mp="3cn7nimxvtqyb" data-mpi={i}>
                  {course.featured && <span className="absolute -top-[9px] left-6 bg-gold-light text-dark font-body font-medium text-[9px] tracking-[3px] uppercase px-3 py-1 z-20" data-id="element-39" data-mp="3aa5hvifeylp8">
                      {t.academy.mostRequested}
                    </span>}

                  <div className="group relative mb-6 -mx-6 -mt-6 sm:-mx-7 sm:-mt-7 overflow-hidden" data-id="element-40" data-mp="3p7ed4n7u89m8">
                    <motion.div initial={{
                  clipPath: 'inset(0 0 100% 0)'
                }} whileInView={{
                  clipPath: 'inset(0 0 0% 0)'
                }} viewport={{
                  once: true,
                  margin: '-40px'
                }} transition={{
                  duration: 1.05,
                  ease: [0.22, 1, 0.36, 1],
                  delay: 0.08 * i
                }} className={`relative overflow-hidden bg-dark ${course.featured ? 'aspect-[4/5] sm:aspect-[1/1]' : 'aspect-[4/5] sm:aspect-[5/5]'}`} data-id="element-41" data-mp="3t883dn8r60ia">
                      <motion.img src={course.image} alt={course.name} loading="lazy" initial={{
                    scale: 1.16
                  }} whileInView={{
                    scale: 1
                  }} viewport={{
                    once: true,
                    margin: '-40px'
                  }} transition={{
                    duration: 1.8,
                    ease: [0.22, 1, 0.36, 1],
                    delay: 0.08 * i
                  }} className="absolute inset-0 w-full h-full object-cover object-[50%_35%] transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]" data-id="element-42" data-mp="2o31pu9jbvf75" />

                      <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-dark via-dark/15 to-transparent" data-id="element-43" data-mp="3dvkcou7l9gv" />

                      <span aria-hidden className="pointer-events-none absolute inset-3 border border-gold-light/30 transition-all duration-700 group-hover:inset-2 group-hover:border-gold-light/60" data-id="element-44" data-mp="3uovmhemh10zw" />

                      {course.insetImage && <motion.img src={course.insetImage} alt="" aria-hidden="true" loading="lazy" initial={{
                    opacity: 0,
                    y: 18,
                    rotate: -3
                  }} whileInView={{
                    opacity: 1,
                    y: 0,
                    rotate: -3
                  }} viewport={{
                    once: true,
                    margin: '-40px'
                  }} transition={{
                    duration: 0.8,
                    delay: 0.65,
                    ease: [0.22, 1, 0.36, 1]
                  }} className="absolute bottom-5 right-5 w-20 sm:w-28 aspect-[3/4] object-cover shadow-[0_10px_30px_rgba(0,0,0,0.45)] border border-gold-light/40 transition-transform duration-700 group-hover:rotate-0 group-hover:-translate-y-1" data-id="element-45" data-mp="1q9a7nu8unvn3" />}
                    </motion.div>
                  </div>

                  <div className="relative z-10 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2" data-id="element-46" data-mp="nv2n6uthpdkl">
                    <div data-id="element-47" data-mp="ix9h01s6sjkb">
                      <h4 className="font-serif font-light text-warm-white text-xl sm:text-[22px] leading-tight" data-id="element-48" data-mp="xkcei8gies7n">
                        {course.name}
                      </h4>
                      {course.duration && <p className="flex items-center gap-2 mt-2 font-body font-light text-cream/60 text-[11px] tracking-[1.5px] uppercase" data-id="element-49" data-mp="16l0uv9tdl4np">
                          <Clock className="w-3.5 h-3.5 text-gold-light" strokeWidth={1.5} data-id="element-50" data-mp="12ie0b15bl5mt" />
                          {course.duration}
                        </p>}
                    </div>

                    <p className="flex items-baseline gap-1.5" data-id="element-51" data-mp="1bju9j5kef1pg">
                      {course.priceNote && <span className="font-body font-light text-cream/50 text-[10px] tracking-[2px] uppercase" data-id="element-52" data-mp="3ia1uiosy8k1x">
                          {course.priceNote}
                        </span>}
                      <span className="font-serif font-light text-gold-light text-[30px] leading-none" data-id="element-53" data-mp="1stptgelrl0qc">
                        {course.price}
                      </span>
                    </p>
                  </div>

                  <div className="mt-5 pt-5 border-t border-gold/15" data-id="element-54" data-mp="1j4ekcryvnw2g">
                    <ul className="flex flex-wrap gap-x-6 gap-y-2.5" data-id="element-55" data-mp="1o2puz4wzyalq">
                      {course.includes.map(item => <li key={item} className="flex items-center gap-2" data-id="element-56" data-mp="1a2yduvb6oe3i">
                          <Check className="w-3.5 h-3.5 text-gold-light flex-shrink-0" strokeWidth={2} data-id="element-57" data-mp="1a51bo1ikvbis" />
                          <span className="font-body font-light text-cream/80 text-xs tracking-[0.3px]" data-id="element-58" data-mp="1ezoqf8ns4rk6">
                            {item}
                          </span>
                        </li>)}
                    </ul>
                  </div>
                </motion.li>)}
            </ul>

            <div className="mt-8 flex flex-col items-start gap-4" data-id="element-59" data-mp="2ugp4yb3gwlvt">
              <motion.a href={whatsappHref} target="_blank" rel="noreferrer" whileHover={{
              y: -2
            }} whileTap={{
              scale: 0.97
            }} transition={{
              duration: 0.25,
              ease: [0.22, 1, 0.36, 1]
            }} className="metallic-reserve-button inline-flex items-center gap-3 px-9 py-4 text-white font-body font-semibold text-[10px] tracking-[4px] uppercase outline-none focus-visible:ring-2 focus-visible:ring-gold-light focus-visible:ring-offset-2 focus-visible:ring-offset-dark sm:px-11" data-id="element-60" data-mp="2zglcgkbdivg4">
                <MessageCircle className="w-4 h-4" strokeWidth={1.75} aria-hidden data-id="element-61" data-mp="zj6vdv2qvmio" />
                {t.academy.cta}
              </motion.a>

              <p className="font-body font-light text-cream/45 text-[11px] leading-relaxed italic" data-id="element-62" data-mp="uknazz3suisf">
                {t.academy.note}
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </section>;
}